import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, TextField, MenuItem,
  LinearProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Card, CardContent,
  CardActions, InputAdornment, FormControlLabel, Switch,
} from '@mui/material';
import { StickyNote2, Add, Edit, Delete, PushPin, Search, Close } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const CATEGORY_CFG: Record<string, { color: string; bg: string }> = {
  performance:   { color: '#1565c0', bg: '#e3f2fd' },
  conduct:       { color: '#c62828', bg: '#ffebee' },
  attendance:    { color: '#e65100', bg: '#fff3e0' },
  development:   { color: '#2e7d32', bg: '#e8f5e9' },
  recognition:   { color: '#f57f17', bg: '#fffde7' },
  general:       { color: '#37474f', bg: '#f5f5f5' },
  confidential:  { color: '#6a1b9a', bg: '#f3e5f5' },
};

const CATEGORIES = Object.keys(CATEGORY_CFG);

interface Note {
  id: string; employeeId: string; authorId: string; category: string;
  title: string; content: string; isConfidential: boolean;
  relatedDate?: string; tags?: string[]; isPinned: boolean;
  createdAt?: string;
  author?: { firstName: string; lastName: string };
  employee?: { firstName: string; lastName: string };
}

const emptyForm = (employeeId = '') => ({
  employeeId, category: 'general', title: '', content: '',
  isConfidential: true, relatedDate: '', tags: [] as string[],
});

export default function EmployeeNotes({ employeeId: propEmpId }: { employeeId?: string }) {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager', 'manager'].includes(user?.role ?? '');
  const myEmpId = user?.employee?.id;

  const targetId = propEmpId || myEmpId || '';

  const [notes, setNotes] = useState<Note[]>([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formDlg, setFormDlg] = useState<any>(null);
  const [deleteDlg, setDeleteDlg] = useState<Note | null>(null);

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true); setError('');
    try {
      const data = await apiFetch(`/api/employee-notes/employee/${targetId}`);
      setNotes(data || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [targetId]);

  useEffect(() => { load(); }, [load]);

  const filtered = notes.filter(n => {
    if (catFilter && n.category !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return n.title.toLowerCase().includes(q) || n.content.toLowerCase().includes(q);
    }
    return true;
  });

  const save = async () => {
    if (!formDlg?.title || !formDlg?.content) return;
    try {
      formDlg.id
        ? await apiFetch(`/api/employee-notes/${formDlg.id}`, { method: 'PATCH', body: JSON.stringify(formDlg) })
        : await apiFetch('/api/employee-notes', { method: 'POST', body: JSON.stringify(formDlg) });
      setFormDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const pin = async (id: string) => {
    try { await apiFetch(`/api/employee-notes/${id}/pin`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const del = async () => {
    if (!deleteDlg) return;
    try {
      await apiFetch(`/api/employee-notes/${deleteDlg.id}`, { method: 'DELETE' });
      setDeleteDlg(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <Box>
      {/* Header — only shown when used as standalone page */}
      {!propEmpId && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StickyNote2 sx={{ fontSize: 32, color: '#f57f17' }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>Employee Notes</Typography>
              <Typography variant="body2" color="text.secondary">Private manager/HR notes on employee records</Typography>
            </Box>
          </Box>
          {isHR && (
            <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#f57f17' }}
              onClick={() => setFormDlg(emptyForm(targetId))}>Add Note</Button>
          )}
        </Box>
      )}

      {propEmpId && isHR && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography fontWeight={700}>HR Notes</Typography>
          <Button size="small" startIcon={<Add />} onClick={() => setFormDlg(emptyForm(propEmpId))}>Add Note</Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Search + filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField placeholder="Search notes…" size="small" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label="All" size="small" onClick={() => setCatFilter('')}
            color={!catFilter ? 'primary' : 'default'} variant={!catFilter ? 'filled' : 'outlined'} />
          {CATEGORIES.map(c => (
            <Chip key={c} label={c} size="small" onClick={() => setCatFilter(c === catFilter ? '' : c)}
              sx={catFilter === c ? { bgcolor: CATEGORY_CFG[c].bg, color: CATEGORY_CFG[c].color, fontWeight: 700 } : {}}
              variant={catFilter === c ? 'filled' : 'outlined'} />
          ))}
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Notes grid */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {filtered.map(note => {
          const cfg = CATEGORY_CFG[note.category] ?? CATEGORY_CFG.general;
          const canEdit = isHR || note.authorId === myEmpId;
          return (
            <Card key={note.id} sx={{ border: `1px solid ${cfg.bg}`, bgcolor: note.isPinned ? '#fffde7' : 'white' }}>
              <CardContent sx={{ pb: '4px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {note.isPinned && <PushPin sx={{ fontSize: 14, color: '#f57f17' }} />}
                    <Typography fontWeight={700}>{note.title}</Typography>
                    <Chip label={note.category} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: '0.65rem' }} />
                    {note.isConfidential && <Chip label="🔒 Confidential" size="small" sx={{ fontSize: '0.6rem', height: 18 }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }}>{fmtDate(note.createdAt)}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {note.content}
                </Typography>
                {note.relatedDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    📅 Related date: {note.relatedDate}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  By: {note.author?.firstName} {note.author?.lastName}
                </Typography>
              </CardContent>
              {canEdit && (
                <CardActions sx={{ pt: 0, pb: 0.5, px: 2 }}>
                  <Tooltip title={note.isPinned ? 'Unpin' : 'Pin'}><IconButton size="small" onClick={() => pin(note.id)}><PushPin fontSize="small" sx={{ color: note.isPinned ? '#f57f17' : undefined }} /></IconButton></Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => setFormDlg({ ...note })}><Edit fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteDlg(note)}><Delete fontSize="small" /></IconButton></Tooltip>
                </CardActions>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && !loading && (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <StickyNote2 sx={{ fontSize: 40, opacity: 0.3 }} />
            <Typography>{search || catFilter ? 'No notes match filter.' : 'No notes yet.'}</Typography>
          </Paper>
        )}
      </Box>

      {/* ── FORM DIALOG ── */}
      <Dialog open={!!formDlg} onClose={() => setFormDlg(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formDlg?.id ? 'Edit Note' : 'Add Note'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setFormDlg(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title *" fullWidth value={formDlg?.title ?? ''} onChange={e => setFormDlg((p: any) => ({ ...p, title: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Category" select fullWidth value={formDlg?.category ?? 'general'}
                onChange={e => setFormDlg((p: any) => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField label="Related Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={formDlg?.relatedDate ?? ''} onChange={e => setFormDlg((p: any) => ({ ...p, relatedDate: e.target.value }))} />
            </Box>
            <TextField label="Content *" fullWidth multiline rows={5} value={formDlg?.content ?? ''}
              onChange={e => setFormDlg((p: any) => ({ ...p, content: e.target.value }))} />
            <FormControlLabel control={<Switch checked={formDlg?.isConfidential ?? true}
              onChange={e => setFormDlg((p: any) => ({ ...p, isConfidential: e.target.checked }))} />}
              label="Confidential (HR/Manager only)" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormDlg(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#f57f17' }} onClick={save}
            disabled={!formDlg?.title?.trim() || !formDlg?.content?.trim()}>
            {formDlg?.id ? 'Update' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ── */}
      <Dialog open={!!deleteDlg} onClose={() => setDeleteDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Note?</DialogTitle>
        <DialogContent><Typography>Delete "<b>{deleteDlg?.title}</b>"? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={del}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
